import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader } from "@mantine/core";
import { IconAlertTriangleFilled, IconPlus, IconX } from "@tabler/icons-react";
import ConversationInterface from "../ConversationInterface";
import ConversationActions from "../ConversationActions";
import MaterialThreeDotMenu from "../MaterialThreeDotMenu";
import ConversationIcon, {
	userSourceName,
	aiSourceName,
	formatTimestamp,
} from "../ChatInterface/ConversationIcon";
import CitationsSidebar from "./CitationsSidebar";
import AiAddContextModal from "./AiAddContextModal";
import { contextItemIcon } from "./contextItemIcon";
import { contextItemKey } from "./contextItemUtils";
import DocumentSlidingPane from "../DocumentPreviewRightPane";
import useDocumentPreviewPane from "../useDocumentPreviewPane";
import {
	getAiSession,
	sendAiChatMessage,
	pollAiChatMessage,
	updateAiSessionContext,
	cancelAiChatMessage,
} from "../../actions/aiChatActions";
import { toggleAiCitationsSidebar } from "../../actions/browserLocalstorageActions";
import {
	randomFromArray,
	getFirstName,
	getAiOpeningGreeting,
} from "../../utilities/utilities";
import "./MatterAiPane.css";

const GREETING_HEADLINES = [
	"How can I help with this Matter?",
	"What would you like to know about this Matter?",
	"Ask me anything about this Matter.",
	"Ready to dig into the details.",
	"Hi {NAME}, what are we working on today?",
];

const FULL_SCOPE_GREETING_HEADLINES = [
	"How can I help across your Matters?",
	"What would you like to know?",
	"Ask me anything about your firm's Matters.",
	"Ready to dig into the details.",
	"Hi {NAME}, what are we working on today?",
];

const MatterAiGreeting = ({ fullScope }) => {
	const userDisplayName = useSelector((state) => state.user.userDisplayName);
	const [headline] = useState(() =>
		randomFromArray(
			fullScope ? FULL_SCOPE_GREETING_HEADLINES : GREETING_HEADLINES
		).replace("{NAME}", getFirstName(userDisplayName))
	);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				flexGrow: 1,
				textAlign: "center",
				padding: "32px 24px",
			}}
		>
			<h1
				style={{
					fontSize: 28,
					fontWeight: 700,
					margin: "0 0 16px",
				}}
			>
				{headline}
			</h1>
			<p
				style={{
					fontSize: 14,
					color: "#666",
					maxWidth: 520,
					margin: 0,
					lineHeight: 1.6,
				}}
			>
				{fullScope
					? "Ask anything about your firm's matters and documents. LexWorkplace will search across all your Matters, emails, and notes to find answers and cite its sources."
					: "Ask anything about this Matter, its content, or any subject covered within it. LexWorkplace will search across your documents, emails, and notes to find answers and cite its sources."}
			</p>
		</div>
	);
};

const dbMessageToUiMessage = ({ role, content, createdAt }) => ({
	source: role === "user" ? userSourceName : aiSourceName,
	content,
	timestamp: formatTimestamp(createdAt),
	animateTyping: false,
});

const addContextButtonStyle = {
	display: "flex",
	alignItems: "center",
	gap: 4,
	fontSize: 13,
	padding: "6px 10px",
	borderRadius: 16,
	border: "1px solid var(--lw-medium-grey)",
	background: "#fff",
	cursor: "pointer",
};

const AddedContextChip = ({ item, onRemove, measuring }) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: 4,
			fontSize: 13,
			padding: "6px 10px",
			borderRadius: 16,
			border: "1px solid var(--lw-medium-grey)",
			background: "#f6f6f6",
			maxWidth: 240,
			flexShrink: 0,
		}}
	>
		{contextItemIcon(item.itemType, item.title)}
		<span
			style={{
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			}}
		>
			{item.title}
		</span>
		<button
			onClick={measuring ? undefined : () => onRemove(item)}
			disabled={measuring}
			aria-label={`Remove ${item.title}`}
			style={{
				display: "flex",
				background: "none",
				border: "none",
				cursor: "pointer",
				padding: 0,
				flexShrink: 0,
			}}
		>
			<IconX size={13} />
		</button>
	</div>
);

// Pills should never wrap to a second line. We render a hidden clone at full
// width to measure whether every chip fits on one line; if not, we collapse
// to a single "N items" summary pill instead.
const AddedContextChips = ({ addedContext, onAdd, onRemove, onFocus }) => {
	const containerRef = useRef(null);
	const measureRef = useRef(null);
	const [fitsInline, setFitsInline] = useState(true);

	useLayoutEffect(() => {
		const checkFit = () => {
			if (!containerRef.current || !measureRef.current) return;
			setFitsInline(
				measureRef.current.scrollWidth <=
					containerRef.current.clientWidth
			);
		};
		checkFit();
		if (typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(checkFit);
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, [addedContext]);

	const showPills = addedContext.length <= 1 || fitsInline;

	return (
		<div ref={containerRef} style={{ position: "relative" }}>
			<div
				ref={measureRef}
				aria-hidden="true"
				style={{
					position: "absolute",
					visibility: "hidden",
					top: 0,
					left: 0,
					display: "flex",
					flexWrap: "nowrap",
					gap: 8,
				}}
			>
				<button disabled style={addContextButtonStyle}>
					<IconPlus size={14} />
					Add Context
				</button>
				{addedContext.map((item) => (
					<AddedContextChip
						key={contextItemKey(item)}
						item={item}
						measuring
					/>
				))}
			</div>
			<div
				style={{
					display: "flex",
					flexWrap: "nowrap",
					overflow: "hidden",
					gap: 8,
					marginBottom: 8,
				}}
			>
				<button
					onClick={onAdd}
					style={{ ...addContextButtonStyle, flexShrink: 0 }}
				>
					<IconPlus size={14} />
					Add Context
				</button>
				{addedContext.length === 0 ? null : showPills ? (
					addedContext.map((item) => (
						<AddedContextChip
							key={contextItemKey(item)}
							item={item}
							onRemove={onRemove}
						/>
					))
				) : (
					<button
						onClick={onFocus}
						style={{
							...addContextButtonStyle,
							background: "#f6f6f6",
							flexShrink: 0,
						}}
					>
						{addedContext.length} items
					</button>
				)}
			</div>
		</div>
	);
};

const MatterAiChat = ({
	matterID,
	sessionId,
	onNewSession,
	onSessionTitleChange,
	basePath,
	fullScope,
}) => {
	const chatBasePath = basePath ?? `/matters/${matterID}/ai`;
	const isNewChat = sessionId === "new";
	const location = useLocation();
	const preloaded = location.state?.preloadedMessages;
	const [messages, setMessages] = useState(preloaded || []);
	const [citationGroups, setCitationGroups] = useState(
		location.state?.preloadedCitationGroups || []
	);
	const [currentSessionId, setCurrentSessionId] = useState(
		isNewChat ? null : sessionId
	);
	const [loading, setLoading] = useState(!isNewChat && !preloaded);
	const [sessionNotFound, setSessionNotFound] = useState(false);
	const [lastMessageInError, setLastMessageInError] = useState(false);
	const [filteredGroupIndex, setFilteredGroupIndex] = useState(null);
	const [addedContext, setAddedContext] = useState(
		location.state?.preloadedAddedContext || []
	);
	const [addContextModalOpen, setAddContextModalOpen] = useState(false);
	const [focusAddedContextTick, setFocusAddedContextTick] = useState(0);
	const { previewOpen, previewSelectedRow, openItemPreview, closePreview } =
		useDocumentPreviewPane();
	const dispatch = useDispatch();
	const userDisplayName = useSelector((state) => state.user.userDisplayName);
	const citationsSidebarCollapsed = useSelector(
		(state) => state.browserLocalstorage.aiCitationsSidebarCollapsed
	);
	const recentMatterIDs = useSelector(
		(state) => state.browserLocalstorage.recentlyViewedMatters
	);
	const showSourcesForGroup = (idx) => {
		setFilteredGroupIndex(idx);
		if (citationsSidebarCollapsed) dispatch(toggleAiCitationsSidebar());
	};

	const focusAddedContext = () => {
		if (citationsSidebarCollapsed) dispatch(toggleAiCitationsSidebar());
		setFocusAddedContextTick((t) => t + 1);
	};

	const citationItemTypes = {
		Document: "File",
		Email: "Email",
		"Matter Note": "Note",
		Matter: "Matter",
	};
	const openCitationPreview = ({ itemType, id, title }) => {
		openItemPreview({ type: citationItemTypes[itemType], id, title });
	};

	const addedContextToPayload = (items) => ({
		matterIds: items
			.filter((i) => i.itemType === "Matter")
			.map((i) => i.id),
		documentIds: items
			.filter((i) => i.itemType === "Document")
			.map((i) => i.id),
		emailIds: items.filter((i) => i.itemType === "Email").map((i) => i.id),
		matterNoteIds: items
			.filter((i) => i.itemType === "Matter Note")
			.map((i) => i.id),
	});

	const handleAddContext = (newItems) => {
		setAddedContext((prev) => {
			const existingKeys = new Set(prev.map(contextItemKey));
			const merged = [
				...prev,
				...newItems.filter((i) => !existingKeys.has(contextItemKey(i))),
			];
			if (currentSessionId) {
				updateAiSessionContext({
					sessionId: currentSessionId,
					addedContext: addedContextToPayload(merged),
				}).catch(() => {});
			}
			return merged;
		});
	};

	const handleRemoveContext = (itemToRemove) => {
		setAddedContext((prev) => {
			const next = prev.filter(
				(i) => contextItemKey(i) !== contextItemKey(itemToRemove)
			);
			if (currentSessionId) {
				updateAiSessionContext({
					sessionId: currentSessionId,
					addedContext: addedContextToPayload(next),
				}).catch(() => {});
			}
			return next;
		});
	};

	const pollIntervalRef = useRef(null);
	const stopRequestedRef = useRef(false);
	const navigate = useNavigate();

	useEffect(() => {
		return () => clearInterval(pollIntervalRef.current);
	}, []);

	useEffect(() => {
		if (isNewChat || preloaded) return;
		getAiSession(sessionId, matterID)
			.then(
				({
					found,
					messages: dbMessages,
					addedContext: sessionContext,
				}) => {
					if (!found) {
						setSessionNotFound(true);
					} else {
						setAddedContext(sessionContext || []);
						setMessages(dbMessages.map(dbMessageToUiMessage));
						const groups = [];
						let lastUserContent = null;
						for (const m of dbMessages) {
							if (m.role === "user") {
								lastUserContent = m.content;
							} else if (m.role === "assistant") {
								groups.push({
									citations: m.citations || [],
									timestamp: formatTimestamp(m.createdAt),
									collapsed: true,
									userPrompt: lastUserContent,
								});
							}
						}
						if (groups.length > 0)
							groups[groups.length - 1].collapsed = false;
						setCitationGroups(groups);
					}
					setLoading(false);
				}
			)
			.catch(() => setLoading(false));
	}, [sessionId]);

	useEffect(() => {
		// preloadedMessages/preloadedCitationGroups/preloadedAddedContext are
		// a one-time seed passed via navigate() right after a new session's
		// first reply. React Router persists that state in
		// window.history.state, which survives a hard refresh — without
		// clearing it here, refreshing this page would keep reusing this
		// stale snapshot forever: replaying the typing animation on an old
		// message, and skipping the real DB fetch above entirely.
		if (!preloaded) return;
		navigate(location.pathname, { replace: true, state: null });
	}, [preloaded, navigate, location.pathname]);

	const appendMessage = (
		source,
		content,
		timestamp = formatTimestamp(new Date())
	) => {
		setMessages((prev) => [
			...prev,
			{
				source,
				content,
				animateTyping: source === aiSourceName,
				timestamp,
			},
		]);
		return timestamp;
	};

	const replaceThinkingMessage = (content, timestamp) => {
		setMessages((prev) =>
			prev.map((m) =>
				m.thinking
					? {
							source: aiSourceName,
							content,
							timestamp,
							animateTyping: true,
						}
					: m
			)
		);
	};

	const removeThinkingMessage = () => {
		setMessages((prev) => prev.filter((m) => !m.thinking));
	};

	const toggleCitationGroup = (index) => {
		setCitationGroups((prev) =>
			prev.map((g, i) => {
				if (i !== index) return g;
				const expanding = g.collapsed;
				return { ...g, collapsed: !g.collapsed, pinned: expanding };
			})
		);
	};

	const submitQuestion = (userPrompt) => {
		stopRequestedRef.current = false;
		const isFirstQuery = messages.length === 0;
		const userMessageTimestamp = appendMessage(userSourceName, userPrompt);
		setMessages((prev) => [
			...prev,
			{
				source: aiSourceName,
				content: isFirstQuery
					? `${getAiOpeningGreeting(userDisplayName)}\n\nThinking...`
					: "Thinking...",
				animateTyping: false,
				thinking: true,
			},
		]);
		setLoading(true);
		setLastMessageInError(false);

		const isNewSession = !currentSessionId;

		sendAiChatMessage({
			matterIds: fullScope ? [] : [matterID],
			sessionId: currentSessionId,
			message: userPrompt,
			// only consumed by the backend when creating a brand-new session —
			// once a session exists, changes go through updateAiSessionContext instead
			addedContext: isNewSession
				? addedContextToPayload(addedContext)
				: undefined,
		})
			.then(({ sessionId: returnedSessionId, userMessageId }) => {
				const resolvedSessionId = currentSessionId || returnedSessionId;
				if (stopRequestedRef.current) {
					// user hit Stop before this initial request even resolved —
					// we now finally have a sessionId to tell the backend to cancel
					cancelAiChatMessage(resolvedSessionId).catch(() => {});
					if (isNewSession) setCurrentSessionId(resolvedSessionId);
					return;
				}
				if (isNewSession) {
					setCurrentSessionId(resolvedSessionId);
				}
				let pollCount = 0;
				// 2.5 min — longer than the backend's agent loop time budget
				const MAX_POLLS = 75;
				pollIntervalRef.current = setInterval(() => {
					if (stopRequestedRef.current) {
						clearInterval(pollIntervalRef.current);
						return;
					}
					pollCount++;
					if (pollCount > MAX_POLLS) {
						clearInterval(pollIntervalRef.current);
						setLoading(false);
						removeThinkingMessage();
						setLastMessageInError(true);
						return;
					}
					pollAiChatMessage({
						sessionId: resolvedSessionId,
						afterMessageId: userMessageId,
					})
						.then(({ pending, message }) => {
							if (stopRequestedRef.current) return;
							if (!pending) {
								clearInterval(pollIntervalRef.current);
								setLoading(false);
								const ts = formatTimestamp(message.createdAt);
								const newGroup = {
									citations: message.citations || [],
									timestamp: ts,
									collapsed: false,
									userPrompt,
								};
								if (isNewSession) {
									navigate(
										`${chatBasePath}/${resolvedSessionId}`,
										{
											replace: true,
											state: {
												preloadedMessages: [
													{
														source: userSourceName,
														content: userPrompt,
														animateTyping: false,
														timestamp:
															userMessageTimestamp,
													},
													{
														source: aiSourceName,
														content:
															message.content,
														timestamp: ts,
														animateTyping: true,
													},
												],
												preloadedCitationGroups: [
													newGroup,
												],
												preloadedAddedContext:
													addedContext,
											},
										}
									);
									onNewSession?.();
								} else {
									if (message.title) {
										onSessionTitleChange?.(
											resolvedSessionId,
											message.title
										);
									}
									replaceThinkingMessage(message.content, ts);
									setCitationGroups((prev) => [
										...prev.map((g) =>
											g.pinned
												? g
												: { ...g, collapsed: true }
										),
										{
											...newGroup,
											collapsed: false,
											pinned: false,
										},
									]);
								}
							}
						})
						.catch(() => {
							if (stopRequestedRef.current) return;
							clearInterval(pollIntervalRef.current);
							setLoading(false);
							removeThinkingMessage();
							setLastMessageInError(true);
						});
				}, 2000);
			})
			.catch(() => {
				if (stopRequestedRef.current) return;
				setLoading(false);
				removeThinkingMessage();
				setLastMessageInError(true);
			});
	};

	const stopResponding = () => {
		stopRequestedRef.current = true;
		clearInterval(pollIntervalRef.current);
		setLoading(false);
		removeThinkingMessage();
		appendMessage(aiSourceName, "You stopped this response.");
		if (currentSessionId) {
			cancelAiChatMessage(currentSessionId).catch(() => {});
		}
	};

	if (loading && messages.length === 0) {
		return (
			<div style={{ padding: 24, textAlign: "center" }}>
				<Loader size={28} />
			</div>
		);
	}

	const hasMessages = messages.length > 0;
	const conversationActionItems = ConversationActions({
		messages,
		matterID,
		dispatch,
		markdownAiResponses: true,
	});

	return (
		<>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					flexGrow: 1,
					overflow: "hidden",
				}}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						flexGrow: 1,
						overflow: "hidden",
						padding: "0 16px 4px 40px",
					}}
				>
					{messages.length === 0 ? (
						<MatterAiGreeting fullScope={fullScope} />
					) : null}
					<ConversationInterface
						messages={messages}
						submitQuestion={submitQuestion}
						IconComponent={ConversationIcon}
						userTextInputAllowed={!loading && !sessionNotFound}
						awaitingResponse={messages.some((m) => m.thinking)}
						onStop={stopResponding}
						showUnsavedDisclaimer={false}
						hideMistakesDisclaimer
						bubbleRightMargin={56}
						inputAccessory={
							<AddedContextChips
								addedContext={addedContext}
								onAdd={() => setAddContextModalOpen(true)}
								onRemove={handleRemoveContext}
								onFocus={focusAddedContext}
							/>
						}
						scrollAreaOverlay={
							hasMessages ? (
								<div
									style={{
										position: "sticky",
										top: 0,
										height: 0,
										zIndex: 2,
									}}
								>
									<div
										style={{
											position: "absolute",
											top: 12,
											right: 12,
										}}
									>
										<MaterialThreeDotMenu
											className="MatterAiThreeDotTrigger"
											position="relative"
											menuItems={conversationActionItems}
										/>
									</div>
								</div>
							) : null
						}
						messageActions={(message, messageIndex) => {
							if (message.source !== aiSourceName) return null;
							const aiIdx =
								messages
									.slice(0, messageIndex + 1)
									.filter((m) => m.source === aiSourceName)
									.length - 1;
							const group = citationGroups[aiIdx];
							if (!group?.citations?.length) return null;
							return (
								<button
									onClick={() => showSourcesForGroup(aiIdx)}
									style={{
										marginTop: 6,
										fontSize: 12,
										color: "var(--lxw-hyperlink)",
										background: "none",
										border: "none",
										cursor: "pointer",
										padding: 0,
										textDecoration: "underline",
									}}
								>
									Show Sources
								</button>
							);
						}}
						endOfMessagesComponent={
							loading ? (
								<div
									style={{ padding: 24, textAlign: "center" }}
								>
									<Loader size={28} />
								</div>
							) : sessionNotFound ? (
								<div
									style={{ padding: 24, textAlign: "center" }}
								>
									<IconAlertTriangleFilled
										size={24}
										color="var(--lw-darker-red)"
										style={{ verticalAlign: "middle" }}
									/>
									<span style={{ paddingLeft: 4 }}>
										This chat does not exist or you do not
										have access to it.
									</span>
								</div>
							) : lastMessageInError ? (
								<div
									style={{ padding: 24, textAlign: "center" }}
								>
									<IconAlertTriangleFilled
										size={24}
										color="var(--lw-darker-red)"
										style={{ verticalAlign: "middle" }}
									/>
									<span style={{ paddingLeft: 4 }}>
										LexWorkplace AI encountered an error
									</span>
								</div>
							) : null
						}
					/>
				</div>
				<CitationsSidebar
					citationGroups={citationGroups}
					onToggleGroup={toggleCitationGroup}
					filteredGroupIndex={filteredGroupIndex}
					onClearFilter={() => setFilteredGroupIndex(null)}
					onPreviewCitation={openCitationPreview}
					addedContext={addedContext}
					onRemoveContext={handleRemoveContext}
					focusAddedContextTick={focusAddedContextTick}
					fullScope={fullScope}
				/>
			</div>
			{previewSelectedRow ? (
				<DocumentSlidingPane
					openPane={previewOpen}
					selectedRowData={previewSelectedRow}
					closeRightPane={closePreview}
					// render above the Add Context modal (z-index 200) when
					// previewing from within it, instead of behind it
					zIndex={addContextModalOpen ? 300 : 50}
				/>
			) : null}
			<AiAddContextModal
				opened={addContextModalOpen}
				onClose={() => setAddContextModalOpen(false)}
				existingItems={addedContext}
				onAdd={handleAddContext}
				recentMatterIDs={recentMatterIDs}
				onPreview={openCitationPreview}
				previewOpen={previewOpen}
				matterID={fullScope ? null : matterID}
			/>
		</>
	);
};

export default MatterAiChat;
